import React from 'react'
import { Modal } from '@components/ui'
import { AvailabilityStatus } from '@dataTypes/appointment.type'

export type SlotUnavailableContent = {
  title: string
  description: string
}

export type SlotUnavailableNames = {
  patientName?: string
  providerName?: string
}

const personName = (
  person?: {
    first_name?: string
    last_name?: string
  } | null,
): string => `${person?.first_name ?? ''} ${person?.last_name ?? ''}`.trim()

export const slotUnavailableNamesFromAppointment = (
  appointment: {
    patient?: { first_name?: string; last_name?: string } | null
    provider?: { first_name?: string; last_name?: string } | null
    provider_id?: string
  },
  providerList: {
    provider_id: string
    first_name?: string
    last_name?: string
  }[] = [],
): SlotUnavailableNames => {
  const listedProvider = providerList.find(
    (provider) => provider.provider_id === appointment.provider_id,
  )

  return {
    patientName: personName(appointment.patient),
    providerName:
      personName(appointment.provider) || personName(listedProvider),
  }
}

const named = (name: string | undefined, fallback: string): string => {
  const trimmed = name?.trim()
  return trimmed ? trimmed : fallback
}

export const getSlotUnavailableContent = (
  status?: AvailabilityStatus,
  names?: SlotUnavailableNames,
): SlotUnavailableContent => {
  const patientName = named(names?.patientName, 'This patient')
  const providerName = named(names?.providerName, 'This provider')

  switch (status) {
    case AvailabilityStatus.PATIENT_ALREADY_BOOKED:
      return {
        title: 'Patient already booked',
        description: `${patientName} already has an appointment scheduled at this time. Please select a different time slot.`,
      }
    case AvailabilityStatus.NO_WORKING_SCHEDULE:
      return {
        title: 'Outside working hours',
        description: `Dr. ${providerName} is not scheduled to work at this time. Please select a time within their working hours.`,
      }
    case AvailabilityStatus.PROVIDER_UNAVAILABLE:
      return {
        title: 'Provider unavailable',
        description: `Dr. ${providerName} is unavailable at this time. Please select a different time slot.`,
      }
    case AvailabilityStatus.PROVIDER_ALREADY_BOOKED:
      return {
        title: 'Provider already booked',
        description: `Dr. ${providerName} already has an appointment scheduled at this time. Please select a different time slot or provider.`,
      }
    case AvailabilityStatus.ROOM_ALREADY_BOOKED:
      return {
        title: 'Rescheduling unavailable',
        description:
          'This slot is already booked. Please choose a different time.',
      }
    default:
      return {
        title: 'Rescheduling unavailable',
        description:
          'This slot is not available. Please select a different time slot.',
      }
  }
}

export const noAvailableProvidersContent: SlotUnavailableContent = {
  title: 'No available providers',
  description: 'No available providers for this time slot.',
}

type RescheduleUnavailableModalProps = {
  show: boolean
  onHide: () => void
  title: string
  description: string | string[]
}

const RescheduleUnavailableModal: React.FC<RescheduleUnavailableModalProps> = ({
  show,
  onHide,
  title,
  description,
}) => (
  <Modal
    open={show}
    onClose={onHide}
    size="xs"
    title={title}
    description={description}
    primaryLabel="Okay"
    secondaryLabel=""
    onPrimaryClick={onHide}
  />
)

export default RescheduleUnavailableModal
